import * as angular from 'angular';
import * as d3 from 'd3';
import * as _ from 'lodash';

import { ComponentDefinition, ILineChartData, SeriesDefinition, TagDateValue } from '../../models';
import { IDateRangeService, IwidgetIntervalHelperService, IAlarmService, IExportToCsvService, CsvExportOptions } from '../../services';
import { errorTextColor, infoTextColor, warningTextColor } from '../../common/constants';
import { AlarmsEvolutionWidget } from '../../widgets/widgets.index';
import { refreshClick } from '../../common/events';
import { ChartConfigurationOption } from '../../framework';

class AlarmsEvolutionController implements angular.IComponentController {
    data: TagDateValue[];
    isLoading: boolean;
    options: ChartConfigurationOption;
    series: SeriesDefinition<TagDateValue>[] = [];

    readonly boundData: TagDateValue[];
    readonly clicked: ILineChartData;
    readonly item: AlarmsEvolutionWidget;

    private _levelScale = d3.scale.ordinal()
        .domain(['critical', 'warning', 'information'])
        .range([6, 7, 8]);

    static $inject = [
        '$scope',
        'alarmService',
        'dateRangeService',
        'exportToCsvService',
        'widgetIntervalHelperService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _alarmService: IAlarmService,
        private _dateRangeService: IDateRangeService,
        private _exportToCsvService: IExportToCsvService,
        private _widgetIntervalHelperService: IwidgetIntervalHelperService
    ) {
    }

    $onInit() {
        const fmt = d3.format('2.2%');
        this.series = [{
            name: 'Critical',
            color: errorTextColor,
            identifier: '6',
            value: d => d.value,
            selector: (d) => d.secondaryValue.toString(),
            isVisible: true,
            isLineVisible: true,
            pane: 0,
            crosshairFormat: d => fmt(d.value)
        },
        {
            name: 'Warning',
            color: warningTextColor,
            identifier: '7',
            value: d => d.value,
            selector: (d) => d.secondaryValue.toString(),
            isVisible: true,
            isLineVisible: true,
            pane: 1,
            crosshairFormat: d => fmt(d.value)
        },
        {
            name: 'Information',
            color: infoTextColor,
            identifier: '8',
            value: d => d.value,
            selector: (d) => d.secondaryValue.toString(),
            isVisible: true,
            isLineVisible: true,
            pane: 2,
            crosshairFormat: d => fmt(d.value)
        }];
        this.data = [];
        this.options = {
            title: 'Evolution',
            isLegendVisible: false,
            display: {
                yAxisFormat: '2.0%',
                yMin: 0,
                yMax: 1
            },
            isTitleVisible: false
        } as ChartConfigurationOption;

        this.refresh(true);
        this._$scope.$on(refreshClick, () => this.refresh());

        this._$scope.$watch(() => this.clicked, (newValue) => {
            if (newValue) {
                this.series.forEach(s => {
                    s.isVisible = !newValue.isSelected || s.identifier === this._levelScale(newValue.classed);
                });
            }
        });

        if (this.item) {
            const csvExportOptions: CsvExportOptions = {
                hasFirstHeading: false,
                hasGroupName: false,
                hasLegend: false,
                hasShortComment: false,
                hasUnit: false,
                hasValue: false
            };
            this.item.exportToCsv = () => {
                return this._exportToCsvService.exportToCsv(this.series, this.data, this.item.name, csvExportOptions);
            };
        }
    }

    private refresh(force?: boolean) {
        if (this._dateRangeService.initialized()) {
            this.isLoading = true;

            const defaultInterval = this._widgetIntervalHelperService.getCurrentInclusiveInterval();
            const beginDate = defaultInterval.beginDate;
            const endDate = defaultInterval.endDate;
            this._alarmService
                .getEvolutionBetweenDates(beginDate, endDate, force)
                .then(data => {
                    this.data = _.sortBy(data, d => d.timeStamp);
                })
                .finally(() => this.isLoading = false);
        }
    }
}

export const wtAlarmsEvolution: ComponentDefinition = {
    componentId: 'wtAlarmsEvolution',
    controller: AlarmsEvolutionController,
    template: require('./alarmsEvolution.component.html'),
    controllerAs: 'vm',
    bindings: {
        boundDate: '<?',
        clicked: '=?',
        item: '<',
        ratio: '<?'
    }
};
